import { assert, NormalizedNumber } from '@sparkdotfi/common-universal'
import { useSuspenseQuery } from '@tanstack/react-query'
import { UseFormReturn } from 'react-hook-form'
import { useConfig } from 'wagmi'

import { stusdsDepositDataQueryFunction, stusdsDepositDataQueryKey } from './stusds/stusdsDepositDataQuery'
import { savingsV2DepositDataQueryFunction, savingsV2DepositDataQueryKey } from './v2/savingsV2DepositDataQuery'
import { getChainConfigEntry } from '@/config/chain'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { AssetInputSchema } from '@/features/dialogs/common/logic/form'
import { FormFieldsForDialog } from '@/features/dialogs/common/types'
import { SavingsTokenRepository } from '@/features/savings/common/useSavingsTokenRepository'
import { SavingsAccount } from '@/features/savings/logic/savings-converters/types'
import { EPOCH_LENGTH } from '@/features/sparklend/common/data/market-info/consts'
import { useTimestamp } from '@/utils/useTimestamp'

export interface UseFormFieldsForSavingsDepositDialogParams {
  form: UseFormReturn<AssetInputSchema>
  savingsAccount: SavingsAccount
  tokenRepository: SavingsTokenRepository
  chainId: number
}

export function useFormFieldsForSavingsDepositDialog({
  form,
  savingsAccount,
  tokenRepository,
  chainId,
}: UseFormFieldsForSavingsDepositDialogParams): FormFieldsForDialog {
  const wagmiConfig = useConfig()
  const isStusds = savingsAccount.savingsToken.symbol === TokenSymbol('stUSDS')
  const { data: stusdsData } = useSuspenseQuery({
    queryKey: isStusds ? stusdsDepositDataQueryKey({ chainId }) : ['skip-stusds-deposit-data-query'],
    queryFn: () => (isStusds ? stusdsDepositDataQueryFunction({ wagmiConfig, chainId }) : null),
  })
  const isSavingsV2 =
    savingsAccount.savingsToken.symbol === TokenSymbol('spUSDC') ||
    savingsAccount.savingsToken.symbol === TokenSymbol('spUSDT') ||
    savingsAccount.savingsToken.symbol === TokenSymbol('spETH')
  const { data: savingsV2Data } = useSuspenseQuery({
    queryKey: isSavingsV2
      ? savingsV2DepositDataQueryKey({ chainId, savingsToken: savingsAccount.savingsToken })
      : ['skip-savings-v2-deposit-data-query'],
    queryFn: () =>
      isSavingsV2
        ? savingsV2DepositDataQueryFunction({
            wagmiConfig,
            savingsToken: savingsAccount.savingsToken,
            underlyingToken: savingsAccount.underlyingToken,
            chainId,
          })
        : null,
  })
  const depositCap = isStusds ? stusdsData?.cap : savingsV2Data?.depositCap
  const totalSupply = isStusds ? stusdsData?.totalSupply : savingsV2Data?.totalSupply
  const { timestamp } = useTimestamp()

  function changeAsset(newSymbol: TokenSymbol): void {
    form.setValue('symbol', newSymbol)
    form.setValue('value', '')
    form.setValue('isMaxSelected', false)
    form.clearErrors()
  }

  const { nativeAssetInfo } = getChainConfigEntry(chainId)

  const { symbol, value } = form.getValues()
  const token = tokenRepository.findOneBySymbol(symbol)
  const balance = tokenRepository.findOneBalance(symbol)

  const maxValue = (() => {
    if (isSavingsV2 && nativeAssetInfo.nativeAssetSymbol === symbol) {
      const capAwareMaxValue = getCapAwareMaxValue({ timestamp, balance, depositCap, totalSupply, savingsAccount })
      const nativeAssetMaxValue = NormalizedNumber.max(
        balance.minus(nativeAssetInfo.minRemainingNativeAssetBalance),
        NormalizedNumber.ZERO,
      )
      return NormalizedNumber.min(nativeAssetMaxValue, capAwareMaxValue)
    }

    if (isStusds || isSavingsV2) {
      return getCapAwareMaxValue({ timestamp, balance, depositCap, totalSupply, savingsAccount })
    }

    return balance
  })()

  return {
    selectedAsset: token,
    selectedAssetBalance: balance,
    value,
    maxSelectedFieldName: 'isMaxSelected',
    changeAsset,
    maxValue,
  }
}

interface GetCapAwareMaxValueParams {
  timestamp: number
  balance: NormalizedNumber
  savingsAccount: SavingsAccount
  depositCap: NormalizedNumber | undefined
  totalSupply: NormalizedNumber | undefined
}
function getCapAwareMaxValue({
  timestamp,
  balance,
  depositCap,
  totalSupply,
  savingsAccount,
}: GetCapAwareMaxValueParams): NormalizedNumber {
  assert(depositCap && totalSupply, 'depositCap and totalSupply are required')

  const totalAssetsIn1Epoch = savingsAccount.converter.predictAssetsAmount({
    shares: totalSupply,
    timestamp: timestamp + EPOCH_LENGTH,
  })
  const remainingCapBuffered = NormalizedNumber.max(depositCap.minus(totalAssetsIn1Epoch), NormalizedNumber.ZERO)
  return NormalizedNumber.min(remainingCapBuffered, balance)
}
